// useScrollLock — lock the BODY scroll while an overlay/sheet is open.
//
// VIN-2026-66 (#136): the obvious `document.body.style.overflow = 'hidden'`
// does NOT stop touch-drag scrolling in iOS WKWebView — the page behind a
// bottom-sheet or the AI overlay still slid/rubber-banded under it
// (user-reported). The reliable iOS technique is to take the body out of
// flow with `position: fixed` while preserving the current scroll offset via
// a negative `top`, then restore both (and scroll back) on release.
//
// Pass a boolean that is true exactly while the overlay is open. The hook is
// a no-op when false, and safely restores prior inline styles on cleanup, so
// stacking two locks (e.g. a sheet over a page) restores correctly LIFO.
import { useEffect } from 'react'

export function useScrollLock(active) {
  useEffect(() => {
    if (!active) return
    const body = document.body
    const scrollY = window.scrollY || window.pageYOffset || 0
    const prev = {
      position: body.style.position,
      top:      body.style.top,
      left:     body.style.left,
      right:    body.style.right,
      width:    body.style.width,
      overflow: body.style.overflow,
    }
    body.style.position = 'fixed'
    body.style.top      = `-${scrollY}px`
    body.style.left     = '0'
    body.style.right    = '0'
    body.style.width    = '100%'
    body.style.overflow = 'hidden'
    return () => {
      body.style.position = prev.position
      body.style.top      = prev.top
      body.style.left     = prev.left
      body.style.right    = prev.right
      body.style.width    = prev.width
      body.style.overflow = prev.overflow
      // Restore the scroll position the lock froze (the negative-top trick
      // resets scrollY to 0 while fixed).
      window.scrollTo(0, scrollY)
    }
  }, [active])
}

export default useScrollLock
